import classNames from 'classnames'
import type { FocusEvent, KeyboardEvent, MouseEvent, RefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import TabComponent from './Tab'
import styles from './TabMenu.module.scss'
import type { TabMenuProps } from './types'

const handleOnFocus = (event: FocusEvent<HTMLButtonElement, Element>) => {
  event.target?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  })
}

const getTabChildren = ({
  container,
  index,
}: {
  container: RefObject<HTMLDivElement>
  index: number
}) => {
  let response = undefined
  if (container.current) {
    const childrenArray = container.current.children
    response = childrenArray[index] as HTMLElement | undefined
  }
  return response
}

const TabMenu = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  activeIndex = 0,
  tabs,
  className,
  onTabSelected,
}: TabMenuProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const handleOnClick = useCallback(
    (_: MouseEvent<HTMLButtonElement>, index: number) => {
      if (index === activeIndex || tabs[index] === undefined) return
      onTabSelected(index)
    },
    [activeIndex, onTabSelected, tabs]
  )

  const focusOnTab = useCallback(
    (index: number) => {
      const activeChild = getTabChildren({ container: containerRef, index })
      activeChild?.focus()
    },
    [containerRef, getTabChildren]
  )

  const handleOnKeyDown = useCallback(
    (event: KeyboardEvent<Element>, index: number) => {
      let newIndex = 0
      switch (event.key) {
        case 'ArrowRight':
          newIndex = index + 1 < tabs.length ? index + 1 : 0
          focusOnTab(newIndex)
          event.preventDefault()
          break
        case 'ArrowLeft':
          newIndex = index - 1 >= 0 ? index - 1 : tabs.length - 1
          focusOnTab(newIndex)
          event.preventDefault()
          break
        case 'Home':
          newIndex = 0
          focusOnTab(newIndex)
          event.preventDefault()
          break
        case 'End':
          newIndex = tabs.length - 1
          focusOnTab(newIndex)
          event.preventDefault()
          break
        default:
          break
      }
    },
    [tabs, focusOnTab]
  )

  useEffect(() => {
    const activeChild = getTabChildren({ container: containerRef, index: activeIndex })
    if (activeChild) {
      activeChild.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [activeIndex, containerRef])

  return (
    <div className={classNames(styles['pintxo-tab-menu'], className)}>
      <div
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={styles['pintxo-tab-menu__container']}
        role="tablist"
        ref={containerRef}
      >
        {tabs.map((tab, index) => (
          <TabComponent
            key={tab.label}
            onClick={(event) => handleOnClick(event, index)}
            onKeyDown={(event) => handleOnKeyDown(event, index)}
            onFocus={(event) => handleOnFocus(event)}
            selected={index === activeIndex}
            tab={tab}
          />
        ))}
      </div>
    </div>
  )
}

export default TabMenu
